import React, { useEffect, useRef } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import { useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";
import { CONST } from "../config/const";
import { ProductPartnerResultResponse } from "../api/productPartnerResult";

function getInitialMonthHeaders(): string[] {
  const now = new Date();
  const endMonth = now.getMonth() + 1;
  let startYear = now.getFullYear();
  let startMonth = endMonth - 5;
  if (startMonth <= 0) {
    startMonth += 12;
    startYear -= 1;
  }
  const headers: string[] = [];
  for (let i = 0; i < 6; i++) {
    const m = startMonth + i;
    const year = startYear + Math.floor((m - 1) / 12);
    const month = ((m - 1) % 12) + 1;
    headers.push(`${year}-${String(month).padStart(2, "0")}`);
  }
  return headers;
}

const headerSx = {
  backgroundColor: CONST.tableBackgroundColor,
  color: "rgb(70, 70, 70)",
  fontFamily: '"Shippori Antique", serif',
  fontWeight: "bold",
  fontSize: "14px",
  whiteSpace: "nowrap" as const,
};

const bodySx = {
  fontFamily: '"Shippori Antique", serif',
  fontSize: "12px",
  color: "rgb(70, 70, 70)",
};

interface TableProductPartnerResultProps {
  data: ProductPartnerResultResponse | null;
  displayMode: "月別" | "日別";
  serchOpen: boolean;
  tableHeight: string;
  totalProductCount: number;
  displayNumber: number;
  setDisplayNumber: (n: number) => void;
  displayNumbers: number[];
  pageNumber: number;
  setPageNumber: (n: number) => void;
  maxPageNumber: number;
  fetchResult: (overrideDisplayNumber?: number, overridePage?: number) => void;
}

function TableProductPartnerResult(props: TableProductPartnerResultProps) {
  const {
    data,
    displayMode,
    serchOpen,
    tableHeight,
    totalProductCount,
    displayNumber,
    setDisplayNumber,
    displayNumbers,
    pageNumber,
    setPageNumber,
    maxPageNumber,
    fetchResult,
  } = props;

  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const tableHeaderRef1 = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (tableHeaderRef1.current && isSmartPhone) {
        tableHeaderRef1.current.style.left = `-${window.scrollX}px`;
      }
    };
    window.addEventListener("scroll", handleScroll);

    // クリーンアップ
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const tableHeaderRef2 = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (tableHeaderRef2.current && isSmartPhone) {
        tableHeaderRef2.current.style.left = `-${window.scrollX}px`;
      }
    };
    window.addEventListener("scroll", handleScroll);

    // クリーンアップ
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const pageList = Array.from({ length: maxPageNumber }, (_, i) => (
    <MenuItem key={i + 1} value={i + 1}>
      {i + 1}
    </MenuItem>
  ));
  const pagedRows = data?.rows ?? [];

  return (
    <Box
      style={{
        width: "100%",
        height: tableHeight,
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        component="div"
        position={isSmartPhone ? "fixed" : "static"}
        style={{
          width: "100%",
          top: isSmartPhone ? "80px" : undefined,
          backgroundColor: "white",
          paddingTop: isSmartPhone ? "8px" : undefined,
          paddingBottom: isSmartPhone ? "8px" : undefined,
          zIndex: isSmartPhone ? 1 : undefined,
        }}
      >
        <Box
          className="shippori-antique-regular"
          sx={{
            display: "inline",
            fontSize: "16px",
            marginLeft: isSmartPhone ? "10px" : undefined,
          }}
        >
          推進実績
          <Box component="span" sx={{ marginLeft: "6px", fontSize: "14px" }}>
            （{totalProductCount.toLocaleString()}商材）
          </Box>
          <IconButton
            sx={{ height: "25px", width: "25px", marginLeft: "5px" }}
            aria-label="detail row"
            size="small"
            onClick={() => fetchResult()}
          >
            <ReplayIcon fontSize="small" />
          </IconButton>
          <FormControl
            sx={{
              marginTop: "8px",
              position: "absolute",
              right: isSmartPhone ? "110px" : serchOpen ? "430px" : "130px",
              width: "80px",
            }}
          >
            <InputLabel
              id="display-number-label"
              sx={{
                fontSize: "14px",
              }}
            >
              商材数
            </InputLabel>
            <Select
              labelId="display-number-label"
              id="display-number"
              value={displayNumber.toString()}
              label="商材数"
              onChange={(e) => {
                const newDN = Number(e.target.value);
                setDisplayNumber(newDN);
                setPageNumber(1);
                fetchResult(newDN, 1);
              }}
              sx={{
                height: "25px",
                textAlign: "end",
                fontSize: "14px",
              }}
            >
              {displayNumbers.map((n, i) => (
                <MenuItem key={"dn" + i} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            disabled={pageNumber === 1}
            aria-label="detail row"
            size="small"
            sx={{
              marginTop: "8px",
              position: "absolute",
              right: isSmartPhone ? "85px" : serchOpen ? "405px" : "105px",
            }}
            onClick={() => {
              const newPg = pageNumber - 1;
              setPageNumber(newPg);
              fetchResult(undefined, newPg);
            }}
          >
            <ArrowBackIosNewIcon
              fontSize="small"
              sx={{ height: "15px", width: "15px" }}
            />
          </IconButton>
          <FormControl
            fullWidth
            sx={{
              marginTop: "8px",
              position: "absolute",
              right: isSmartPhone ? "20px" : serchOpen ? "335px" : "35px",
              width: "70px",
            }}
          >
            <InputLabel
              id="page-number-label"
              sx={{
                fontSize: "14px",
              }}
            >
              ページ
            </InputLabel>
            <Select
              labelId="page-number-label"
              value={pageNumber.toString()}
              label="ページ"
              onChange={(e) => {
                const newPg = Number(e.target.value);
                setPageNumber(newPg);
                fetchResult(undefined, newPg);
              }}
              sx={{
                height: "25px",
                textAlign: "end",
                fontSize: "14px",
              }}
            >
              {pageList}
            </Select>
          </FormControl>
          <IconButton
            disabled={pageNumber === maxPageNumber}
            aria-label="detail row"
            size="small"
            sx={{
              marginTop: "8px",
              position: "absolute",
              right: isSmartPhone ? "0px" : serchOpen ? "310px" : "10px",
            }}
            onClick={() => {
              const newPg = pageNumber + 1;
              setPageNumber(newPg);
              fetchResult(undefined, newPg);
            }}
          >
            <ArrowForwardIosIcon
              fontSize="small"
              sx={{ height: "15px", width: "15px" }}
            />
          </IconButton>
        </Box>
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          margin: isSmartPhone ? undefined : "1px 4px",
          width: isSmartPhone ? undefined : "calc(100% - 8px)",
          height: isSmartPhone ? undefined : "calc(100% - 32px)",
          border: isSmartPhone ? undefined : "1px solid rgb(230, 230, 230)",
          marginTop: isSmartPhone ? "200px" : undefined,
          position: undefined,
          bottom: undefined,
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow
              ref={tableHeaderRef1}
              sx={{
                position: isSmartPhone ? "fixed" : undefined,
                top: isSmartPhone ? "128px" : undefined,
                zIndex: isSmartPhone ? 1 : undefined,
              }}
            >
              <TableCell
                sx={{
                  ...headerSx,
                  width: 220,
                  minWidth: 220,
                  zIndex: isSmartPhone ? 2 : 3,
                }}
              >
                <Box className="table-header">商材名</Box>
              </TableCell>
              <TableCell
                sx={{
                  ...headerSx,
                  width: 200,
                  minWidth: 200,
                  zIndex: isSmartPhone ? 2 : 3,
                }}
              >
                <Box className="table-header">パートナー</Box>
              </TableCell>
              {displayMode === "月別" && (
                <TableCell
                  sx={{
                    ...headerSx,
                    width: 80,
                    minWidth: 80,
                    zIndex: isSmartPhone ? 2 : 3,
                  }}
                >
                  <Box className="table-header">累計合計</Box>
                </TableCell>
              )}
              {displayMode === "日別" && (
                <>
                  <TableCell
                    sx={{
                      ...headerSx,
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                    }}
                  >
                    <Box className="table-header">指定月合計</Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...headerSx,
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                    }}
                  >
                    <Box className="table-header">指定月見込</Box>
                  </TableCell>
                </>
              )}
              {(data ? data.headers : getInitialMonthHeaders()).map(
                (header, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      ...headerSx,
                      width: 80,
                      minWidth: 80,
                      zIndex: 2,
                    }}
                  >
                    <Box className="table-header">{header}</Box>
                  </TableCell>
                ),
              )}
            </TableRow>
            {data && displayMode === "日別" ? (
              <>
                {/* ショット行 */}
                <TableRow
                  ref={tableHeaderRef2}
                  sx={{
                    position: isSmartPhone ? "fixed" : undefined,
                    top: isSmartPhone ? "164px" : undefined,
                    zIndex: isSmartPhone ? 1 : undefined,
                  }}
                >
                  <TableCell
                    rowSpan={2}
                    sx={{
                      position: "sticky" as const,
                      top: "37px",
                      width: 220,
                      minWidth: 220,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: CONST.tableDetailsBackgroundColor,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">合計</Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "37px",
                      width: 200,
                      minWidth: 200,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: CONST.tableDetailsBackgroundColor,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">ショット</Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "37px",
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: CONST.tableDetailsBackgroundColor,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">
                      {data.total_month_total_shot ?? 0}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "37px",
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: CONST.tableDetailsBackgroundColor,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">
                      {data.total_month_forecast_shot ?? 0}
                    </Box>
                  </TableCell>
                  {(data.total_shot ?? []).map((count, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        position: "sticky" as const,
                        top: "37px",
                        width: 80,
                        minWidth: 80,
                        backgroundColor: CONST.tableDetailsBackgroundColor,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">{count}</Box>
                    </TableCell>
                  ))}
                </TableRow>
                {/* ストック行 */}
                <TableRow
                  sx={{
                    position: isSmartPhone ? "fixed" : undefined,
                    top: isSmartPhone ? "164px" : undefined,
                    zIndex: isSmartPhone ? 1 : undefined,
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "74px",
                      width: 200,
                      minWidth: 200,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: CONST.tableDetailsBackgroundColor,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">ストック</Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "74px",
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: CONST.tableDetailsBackgroundColor,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">
                      {data.total_month_total_stock ?? 0}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      position: "sticky" as const,
                      top: "74px",
                      width: 80,
                      minWidth: 80,
                      zIndex: isSmartPhone ? 2 : 3,
                      backgroundColor: CONST.tableDetailsBackgroundColor,
                      ...bodySx,
                    }}
                  >
                    <Box className="table-header">
                      {data.total_month_forecast_stock ?? 0}
                    </Box>
                  </TableCell>
                  {(data.total_stock ?? []).map((count, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        position: "sticky" as const,
                        top: "74px",
                        width: 80,
                        minWidth: 80,
                        backgroundColor: CONST.tableDetailsBackgroundColor,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">{count}</Box>
                    </TableCell>
                  ))}
                </TableRow>
              </>
            ) : (
              data && (
                <>
                  {/* ショット行 */}
                  <TableRow
                    ref={tableHeaderRef2}
                    sx={{
                      position: isSmartPhone ? "fixed" : undefined,
                      top: isSmartPhone ? "164px" : undefined,
                      zIndex: isSmartPhone ? 1 : undefined,
                    }}
                  >
                    <TableCell
                      rowSpan={2}
                      sx={{
                        position: "sticky" as const,
                        top: "37px",
                        width: 220,
                        minWidth: 220,
                        zIndex: isSmartPhone ? 2 : 3,
                        backgroundColor: CONST.tableDetailsBackgroundColor,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">合計</Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky" as const,
                        top: "37px",
                        width: 200,
                        minWidth: 200,
                        zIndex: isSmartPhone ? 2 : 3,
                        backgroundColor: CONST.tableDetailsBackgroundColor,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">ショット</Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky" as const,
                        top: "37px",
                        width: 80,
                        minWidth: 80,
                        zIndex: isSmartPhone ? 2 : 3,
                        backgroundColor: CONST.tableDetailsBackgroundColor,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">
                        {data.total_period_total_shot ?? 0}
                      </Box>
                    </TableCell>
                    {(data.total_shot ?? []).map((count, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          position: "sticky" as const,
                          top: "37px",
                          width: 80,
                          minWidth: 80,
                          backgroundColor: CONST.tableDetailsBackgroundColor,
                          ...bodySx,
                        }}
                      >
                        <Box className="table-header">{count}</Box>
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* ストック行 */}
                  <TableRow
                    sx={{
                      position: isSmartPhone ? "fixed" : undefined,
                      top: isSmartPhone ? "164px" : undefined,
                      zIndex: isSmartPhone ? 1 : undefined,
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky" as const,
                        top: "74px",
                        width: 200,
                        minWidth: 200,
                        zIndex: isSmartPhone ? 2 : 3,
                        backgroundColor: CONST.tableDetailsBackgroundColor,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">ストック</Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky" as const,
                        top: "74px",
                        width: 80,
                        minWidth: 80,
                        zIndex: isSmartPhone ? 2 : 3,
                        backgroundColor: CONST.tableDetailsBackgroundColor,
                        ...bodySx,
                      }}
                    >
                      <Box className="table-header">
                        {data.total_period_total_stock ?? 0}
                      </Box>
                    </TableCell>
                    {(data.total_stock ?? []).map((count, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          position: "sticky" as const,
                          top: "74px",
                          width: 80,
                          minWidth: 80,
                          backgroundColor: CONST.tableDetailsBackgroundColor,
                          ...bodySx,
                        }}
                      >
                        <Box className="table-header">{count}</Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </>
              )
            )}
          </TableHead>
          <TableBody>
            {data &&
              (() => {
                let globalPartnerIdx = 0;
                return pagedRows.map((row, productIdx) =>
                  (row.partners ?? []).map((partner, partnerIdx) => {
                    const productGroupBg =
                      productIdx % 2 === 1
                        ? CONST.tableDetailsBackgroundColor
                        : "white";
                    const currentGlobalIdx = globalPartnerIdx++;
                    const partnerRowBg =
                      currentGlobalIdx % 2 === 1
                        ? CONST.tableBackgroundColor
                        : "white";
                    return (
                      <TableRow
                        key={`${row.product_id}-${partner.sale_company_id}-${partnerIdx}`}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        {partnerIdx === 0 && (
                          <TableCell
                            rowSpan={row.partners.length}
                            sx={{
                              width: 220,
                              minWidth: 220,
                              zIndex: 1,
                              verticalAlign: "middle",
                              backgroundColor: productGroupBg,
                              ...bodySx,
                            }}
                          >
                            {row.high_rank_company_name} / {row.product_name}
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            width: 200,
                            minWidth: 200,
                            zIndex: 1,
                            backgroundColor: partnerRowBg,
                            ...bodySx,
                          }}
                        >
                          {partner.sale_company_name}
                        </TableCell>
                        {displayMode === "月別" && (
                          <TableCell
                            sx={{
                              width: 80,
                              minWidth: 80,
                              zIndex: 1,
                              backgroundColor: partnerRowBg,
                              ...bodySx,
                            }}
                          >
                            {partner.period_total ?? 0}
                          </TableCell>
                        )}
                        {displayMode === "日別" && (
                          <>
                            <TableCell
                              sx={{
                                width: 80,
                                minWidth: 80,
                                zIndex: 1,
                                backgroundColor: partnerRowBg,
                                ...bodySx,
                              }}
                            >
                              {(partner.month_total_shot ?? 0) + (partner.month_total_stock ?? 0)}
                            </TableCell>
                            <TableCell
                              sx={{
                                width: 80,
                                minWidth: 80,
                                zIndex: 1,
                                backgroundColor: partnerRowBg,
                                ...bodySx,
                              }}
                            >
                              {(partner.month_forecast_shot ?? 0) + (partner.month_forecast_stock ?? 0)}
                            </TableCell>
                          </>
                        )}
                        {(partner.counts ?? []).map((count, i) => (
                          <TableCell
                            key={i}
                            sx={{
                              width: 80,
                              minWidth: 80,
                              backgroundColor: partnerRowBg,
                              ...bodySx,
                            }}
                          >
                            {count}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  }),
                );
              })()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default TableProductPartnerResult;
